import { environment } from '../../environments/environment';
import { HighGuideInsights } from '../models/guide/dto/high-guide-insights';

/**
 * A development-only switch for previewing the HighGuide Insights empty states on an account that has data.
 *
 * The empty states are the hardest part of this feature to see: they need an account with a full catalogue but no
 * recommendation activity, which is close to the opposite of every account anyone tests on. This blanks branches of
 * the payload on the way in so the blurred previews render on any account.
 *
 * Usage - the app uses `HashLocationStrategy`, so the query goes after the hash:
 *
 *   /#/home?insightsEmpty=all
 *   /#/home?insightsEmpty=brands,recommendations
 *   /#/home?insightsEmpty=off        clears it
 *
 * Accepted values: `all`, `brands`, `recommendations`, `collections`, `off`. Anything else is ignored.
 *
 * The choice is latched into sessionStorage rather than read from the URL each time. `refresh-session.guard` and
 * the auth component both `router.navigate(['home'])` with no `queryParamsHandling`, which strips the query long
 * before the insights fetch runs - so a URL-only flag reads as empty every time and looks like it does nothing.
 * Latching also means it survives navigating around the app, and dies with the tab.
 *
 * Inert in production builds - nothing is latched and nothing is applied - so this cannot blank a real customer's
 * dashboard even if the URL is shared.
 */
export class HighGuideInsightsDebugUtils {
  static readonly queryParamKey = 'insightsEmpty';

  private static readonly storageKey = 'BudSense-debug-insightsEmpty';
  private static readonly clearValue = 'off';
  private static readonly allBranches = 'all';
  private static readonly brandsBranch = 'brands';
  private static readonly recommendationsBranch = 'recommendations';
  private static readonly collectionsBranch = 'collections';

  /**
   * Mutates the payload rather than rebuilding it: the class carries behaviour the consumers rely on, and a
   * reconstructed copy would have to be re-deserialized to keep it.
   */
  static emptyRequestedBranches(insights: HighGuideInsights): HighGuideInsights {
    if (environment.production || !insights) return insights;
    const branches = HighGuideInsightsDebugUtils.latchedBranches();
    if (!branches.length) return insights;
    const wantsAll = branches.includes(HighGuideInsightsDebugUtils.allBranches);
    if (wantsAll || branches.includes(HighGuideInsightsDebugUtils.brandsBranch)) {
      insights.trendingBrands = [];
    }
    if (wantsAll || branches.includes(HighGuideInsightsDebugUtils.recommendationsBranch)) {
      if (insights.recentRecommendations) insights.recentRecommendations.recommendations = [];
    }
    if (wantsAll || branches.includes(HighGuideInsightsDebugUtils.collectionsBranch)) {
      insights.insightCollections = [];
    }
    return insights;
  }

  /**
   * `window.location.search` is empty under a hash strategy - the query lives inside the fragment - so the fragment
   * is what gets parsed.
   */
  private static branchesFromUrl(): string[] {
    const hash = window?.location?.hash ?? '';
    const queryStart = hash.indexOf('?');
    if (queryStart < 0) return [];
    const value = new URLSearchParams(hash.substring(queryStart + 1)).get(HighGuideInsightsDebugUtils.queryParamKey);
    return HighGuideInsightsDebugUtils.splitBranches(value ?? '');
  }

  private static splitBranches(value: string): string[] {
    return value
      .split(',')
      .map(branch => branch.trim().toLowerCase())
      .filter(branch => branch.length > 0);
  }

  /**
   * The latched choice, refreshed from the URL whenever the query is actually present.
   *
   * Raw sessionStorage rather than `CacheService`: this is a development affordance with no DTO behind it, and it
   * has to be readable before anything else in the app has initialised.
   */
  private static latchedBranches(): string[] {
    const fromUrl = HighGuideInsightsDebugUtils.branchesFromUrl();
    try {
      if (fromUrl.includes(HighGuideInsightsDebugUtils.clearValue)) {
        window?.sessionStorage?.removeItem(HighGuideInsightsDebugUtils.storageKey);
        return [];
      }
      if (fromUrl.length) {
        window?.sessionStorage?.setItem(HighGuideInsightsDebugUtils.storageKey, fromUrl.join(','));
        return fromUrl;
      }
      const stored = window?.sessionStorage?.getItem(HighGuideInsightsDebugUtils.storageKey) ?? '';
      return HighGuideInsightsDebugUtils.splitBranches(stored);
    } catch (_) {
      // Private browsing and blocked site data both throw here. A debug switch is not worth failing the page for.
      return fromUrl;
    }
  }
}
